/* eslint-disable react/prop-types */
import {
  Typography,
  useTheme,
  Box,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { type GridColumns, type GridValidRowModel } from '@mui/x-data-grid';
import { CustomDataGrid } from 'components';
import { colorTheme } from 'theme';
import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { strings } from 'i18n/Strings';
import {
  commonConstantTexts,
  transferInFileStatus,
} from 'constants/constantText';
import { checkSortingValues, dateFormat } from 'utils/GeneralUtil';
import {
  setCurrentPage,
  setSortOrder,
  setSortBy,
} from 'store/component/pagination';
import Buttons from 'components/elements/Buttons';
import { getEmployeeDetailsByEmailOrUniqueId } from 'service/OrderService';
import { setSnackBarFailed, setSnackBarSuccess } from 'store/snackBar';
import { type SearchUserDataType } from 'pages/Orders/TodayOrder';
import {
  addNewTransferRequest,
  updateTransferRequest,
  viewTransferRequests,
} from 'service/RequestsService';
import EditIcon from '@mui/icons-material/Edit';
import TransferInDialogBox from './TransferInDialogBox';
import { CurrencyFormatterAllDecimals } from 'components/helpers/commonFunctions/CurrencyFormater';

interface Request {
  requestId: string;
  requestedRetirementPlanId: string;
  requestedPensionPlanId: string;
  status: string;
  user: User;
  requestedRetirementPlan: { planName: string };
  currentRetirementPlan: { planName: string };
  requestedPensionPlan: { name: string };
  currentPensionPlan: { name: string };
  approvedDate: number | string;
  rejected: number | string;
  pensionProvider?: string;
  pensionProviderBankAccountName?: string;
  estimatedTransferValue?: number | null;
  transferValueReceived?: number | null;
  contributionEmployer?: number | null;
  contributionEmployee?: number | null;
  avc?: number | null;
  previousTransfer?: number | null;
  sourceOfTransfer?: string;
}

interface User {
  userId: string;
  firstName: string;
  lastName: string;
  email: string;
  callingCode: string;
  phone: string;
}

const TransferInRequests = (): JSX.Element => {
  const theme = useTheme();
  const colors = colorTheme(theme.palette.mode);
  const [transferData, setTransferData] = useState<Request[]>([]);
  const dispatch = useDispatch();
  const [pageCount, setPageCount] = useState(null);
  const [loader, setLoader] = useState(false);
  const pageInfo = useSelector(
    (state) => state?.pagination?.transferInRequests,
  );
  const [filters, setFilters] = useState<unknown>({
    startDate: null,
    endDate: null,
    status: '',
    transferType: 'in',
  });
  const [openDialog, setOpenDialog] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [filterSearchValue, setFilterSearchValue] = useState('');
  const [searchedUserData, setSearchedUserData] =
    useState<SearchUserDataType | null>();
  const [pensionProvider, setPensionProvider] = useState('');
  const [pensionProviderBankAccountName, setPensionProviderBankAccountName] =
    useState('');
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingRequest, setEditingRequest] = useState<Request | null>(null);
  const [estimatedTransferValue, setEstimatedTransferValue] = useState<
    number | null
  >(null);
  const [transferValueReceived, setTransferValueReceived] = useState<
    number | null
  >(null);
  const [contributionEmployer, setContributionEmployer] = useState<
    number | null
  >(null);
  const [contributionEmployee, setContributionEmployee] = useState<
    number | null
  >(null);
  const [avc, setAvc] = useState<number | null>(null);
  const [previousTransfer, setPreviousTransfer] = useState<number | null>(null);
  const [sourceOfTransfer, setSourceOfTransfer] = useState('');

  const getAllTransferInRequests = async (): Promise<void> => {
    try {
      const transferRequests = await viewTransferRequests(
        Number(pageInfo?.page) + 1,
        pageInfo?.pageSize,
        pageInfo?.sortBy,
        pageInfo?.sortOrder,
        filterSearchValue,
        filters,
      );
      setTransferData(transferRequests?.requests?.rows);
      setPageCount(transferRequests?.requests?.count);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    void getAllTransferInRequests();
  }, [
    pageInfo?.page,
    pageInfo?.pageSize,
    filterSearchValue,
    pageInfo?.sortBy,
    pageInfo?.sortOrder,
  ]);

  const handleFilterChange = (filterType: string, value: string): void => {
    setFilters({
      ...filters,
      [filterType]: value,
    });
  };

  const handleSearch = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ): Promise<void> => {
    setFilterSearchValue(event.target.value);
  };

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const handleApplyFilter = () => {
    dispatch(
      setCurrentPage({
        key: commonConstantTexts.transferInRequestPage,
        value: 0,
      }),
    );

    void getAllTransferInRequests();
  };

  const handleSearchUserForTranserIn = async (value: string): Promise<void> => {
    try {
      const searchResult = await getEmployeeDetailsByEmailOrUniqueId(value);
      if (searchResult) {
        setSearchedUserData(searchResult);
        dispatch(
          setCurrentPage({
            key: commonConstantTexts.transferInRequestPage,
            value: 0,
          }),
        );
      } else {
        setSearchedUserData(null);
        dispatch(
          setSnackBarFailed({
            snackBarMessage: strings.displayText.noUserDataFound,
          }),
        );
      }
    } catch (error) {
      console.log(error);
      throw error;
    }
  };

  const handleDialogClose = (): void => {
    setOpenDialog(false);
    setSearchValue('');
    setSearchedUserData(null);
  };

  const createTransferInRequest = async (): void => {
    try {
      if (searchedUserData?.userId) {
        await addNewTransferRequest({
          userId: searchedUserData.userId,
          pensionProvider,
          pensionProviderBankAccountName,
          estimatedTransferValue: estimatedTransferValue ?? 0,
          transferValueReceived: transferValueReceived ?? 0,
          contributionEmployer: contributionEmployer ?? 0,
          contributionEmployee: contributionEmployee ?? 0,
          avc: avc ?? 0,
          previousTransfer: previousTransfer ?? 0,
          sourceOfTransfer,
        })
          .then(async () => {
            await getAllTransferInRequests();
            dispatch(
              setSnackBarSuccess({
                snackBarMessage: strings.displayText.transferInRequestCreated,
              }),
            );
            handleDialogClose();
          })
          .catch((err) => {
            dispatch(setSnackBarFailed({ snackBarMessage: err.message }));
            console.log(err);
          });
      }
    } catch (error) {
      console.log(error);
      throw error;
    }
  };

  const handleEditRequest = (request: Request): void => {
    setEditingRequest(request);
    setPensionProvider(request.pensionProvider ?? '');
    setPensionProviderBankAccountName(
      request.pensionProviderBankAccountName ?? '',
    );
    setEstimatedTransferValue(request.estimatedTransferValue ?? null);
    setTransferValueReceived(request.transferValueReceived ?? null);
    setContributionEmployer(request.contributionEmployer ?? null);
    setContributionEmployee(request.contributionEmployee ?? null);
    setAvc(request.avc ?? null);
    setPreviousTransfer(request.previousTransfer ?? null);
    setSourceOfTransfer(request.sourceOfTransfer ?? '');
    setEditDialogOpen(true);
  };

  const handleEditDialogClose = (): void => {
    setEditDialogOpen(false);
    setEditingRequest(null);
    setPensionProvider('');
    setPensionProviderBankAccountName('');
    setEstimatedTransferValue(null);
    setTransferValueReceived(null);
    setContributionEmployer(null);
    setContributionEmployee(null);
    setAvc(null);
    setPreviousTransfer(null);
    setSourceOfTransfer('');
  };

  const updateTransferInRequest = async (): void => {
    setLoader(true);
    try {
      if (editingRequest?.requestId) {
        await updateTransferRequest({
          requestId: editingRequest.requestId,
          pensionProvider,
          pensionProviderBankAccountName,
          estimatedTransferValue: estimatedTransferValue ?? 0,
          transferValueReceived: transferValueReceived ?? 0,
          contributionEmployer: contributionEmployer ?? 0,
          contributionEmployee: contributionEmployee ?? 0,
          avc: avc ?? 0,
          previousTransfer: previousTransfer ?? 0,
          sourceOfTransfer,
        })
          .then(() => {
            dispatch(
              setSnackBarSuccess({
                snackBarMessage:
                  strings.displayText.transferInUpdatedSuccessfully,
              }),
            );

            handleEditDialogClose();
            void getAllTransferInRequests();
          })
          .catch((err) => {
            dispatch(setSnackBarFailed({ snackBarMessage: err.message }));
            console.log(err);
          });
      }
      setLoader(false);
    } catch (error) {
      console.log(error);
      throw error;
    }
  };

  const columns: GridColumns<GridValidRowModel> = [
    {
      field: 'requestId',
      width: 120,
      cellClassName: 'name-column--cell',
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.planChangeRequestId}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row.requestId}
        </Typography>
      ),
    },
    {
      field: 'createdAt',
      width: 150,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.requestedDate}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {dateFormat(row?.createdAt)}
        </Typography>
      ),
    },
    {
      field: 'user.firstName',
      width: 180,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.nameColumn}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row.user.firstName} {row.user.lastName}
        </Typography>
      ),
    },
    {
      field: 'user.email',
      width: 260,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.emailAddress}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row.user.email}
        </Typography>
      ),
    },
    {
      field: 'user.uniqueId',
      width: 220,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.uniqueId}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row.user.uniqueId}
        </Typography>
      ),
    },
    {
      field: 'pensionProvider',
      width: 250,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.pensionProvider}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row.pensionProvider}
        </Typography>
      ),
    },
    {
      field: 'pensionProviderBankAccountName',
      width: 250,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.bankName}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row.pensionProviderBankAccountName || '-'}
        </Typography>
      ),
    },
    {
      field: 'amount',
      width: 250,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.transferValueReceived}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {CurrencyFormatterAllDecimals(row.transferValueReceived) || '-'}
        </Typography>
      ),
    },
    {
      field: 'reconciledDate',
      width: 250,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.reconciledDate}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {dateFormat(row.reconciledDate) || '-'}
        </Typography>
      ),
    },
    {
      field: 'status',
      width: 150,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.statusColumn}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row.status === commonConstantTexts.success
            ? commonConstantTexts.reconciled
            : commonConstantTexts.nonReconciled}
        </Typography>
      ),
    },
  ];

  return (
    <>
      <Grid
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          marginBottom: '20px',
        }}
      >
        <Buttons
          onClick={() => {
            setOpenDialog(true);
          }}
          variant="contained"
          color="primary"
        >
          {strings.displayText.addNew}
        </Buttons>
      </Grid>
      <Box sx={{ display: 'flex', flexDirection: 'column' }}></Box>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={5} />
        <Grid sx={{ minWidth: 100, marginTop: 2, marginRight: 1 }}>
          <TextField
            label={strings.displayText.startDate}
            type="date"
            value={filters.startDate}
            onChange={(e) => {
              handleFilterChange('startDate', e.target.value);
            }}
            InputLabelProps={{
              shrink: true,
            }}
          />
        </Grid>
        <Grid sx={{ minWidth: 100, marginTop: 2, marginRight: 1 }}>
          <TextField
            label={strings.displayText.endDate}
            type="date"
            value={filters.endDate}
            onChange={(e) => {
              handleFilterChange('endDate', e.target.value);
            }}
            InputLabelProps={{
              shrink: true,
            }}
          />
        </Grid>
        <Grid sx={{ minWidth: 200, marginTop: 2, marginRight: 1 }}>
          <FormControl fullWidth>
            <InputLabel id="demo-simple-select-label">Status</InputLabel>
            <Select
              labelId="demo-simple-select-label"
              id="demo-simple-select"
              label="Status"
              value={filters.status}
              onChange={(e) => {
                handleFilterChange('status', e.target.value);
              }}
            >
              {transferInFileStatus.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item>
          <LoadingButton
            type="submit"
            fullWidth
            variant="contained"
            loadingPosition="end"
            onClick={handleApplyFilter}
          >
            {strings.displayText.filter}
          </LoadingButton>
        </Grid>
      </Grid>

      <CustomDataGrid
        columns={columns}
        rows={transferData}
        getRowId={(row: Request) => `${String(row.requestId)}`}
        getRowClassName={(params) =>
          params.indexRelativeToCurrentPage % 2 === 0
            ? commonConstantTexts.evenText
            : commonConstantTexts.oddText
        }
        currentPage={commonConstantTexts.transferInRequestPage}
        pageCount={pageCount}
        searchEnabled={true}
        handleSearch={handleSearch}
        sortModel={checkSortingValues(pageInfo)}
        sortingMode={strings.displayText.sortingServer}
        onSortModelChange={(mode) => {
          dispatch(
            setSortBy({
              key: commonConstantTexts.transferInRequestPage,
              value: mode[0].field,
            }),
          );
          dispatch(
            setSortOrder({
              key: commonConstantTexts.transferInRequestPage,
              value: mode[0].sort,
            }),
          );
          dispatch(
            setCurrentPage({
              key: commonConstantTexts.transferInRequestPage,
              value: 0,
            }),
          );
        }}
        actionBtnText1="edit"
        ActionBtnIcon1={() => <EditIcon />}
        hideActionBtnIcon1={(row) => row.status === commonConstantTexts.success}
        onAddButtonClick={() => {
          setEditDialogOpen(true);
        }}
        onActionBtnClick1={(row, event) => {
          event.stopPropagation();
          handleEditRequest(row);
        }}
      />

      <TransferInDialogBox
        openDialog={openDialog}
        handleDialogClose={handleDialogClose}
        searchValue={searchValue}
        setSearchValue={setSearchValue}
        handleSearchUserForTranserIn={handleSearchUserForTranserIn}
        searchedUserData={searchedUserData}
        pensionProvider={pensionProvider}
        pensionProviderBankAccountName={pensionProviderBankAccountName}
        setPensionProvider={setPensionProvider}
        setPensionProviderBankAccountName={setPensionProviderBankAccountName}
        createTransferInRequest={createTransferInRequest}
        editDialogOpen={editDialogOpen}
        handleEditDialogClose={handleEditDialogClose}
        editingRequest={editingRequest}
        updateTransferInRequest={updateTransferInRequest}
        loader={loader}
        estimatedTransferValue={estimatedTransferValue}
        setEstimatedTransferValue={setEstimatedTransferValue}
        transferValueReceived={transferValueReceived}
        setTransferValueReceived={setTransferValueReceived}
        contributionEmployer={contributionEmployer}
        setContributionEmployer={setContributionEmployer}
        contributionEmployee={contributionEmployee}
        setContributionEmployee={setContributionEmployee}
        avc={avc}
        setAvc={setAvc}
        previousTransfer={previousTransfer}
        setPreviousTransfer={setPreviousTransfer}
        sourceOfTransfer={sourceOfTransfer}
        setSourceOfTransfer={setSourceOfTransfer}
      />
    </>
  );
};

export default TransferInRequests;
